import { useState } from 'react';

interface NSIItem {
    id: number;
    text: string;
}

const NSI_ITEMS: NSIItem[] = [
    { id: 1, text: 'Feeling dizzy' },
    { id: 2, text: 'Loss of balance' },
    { id: 3, text: 'Poor coordination, clumsy' },
    { id: 4, text: 'Headaches' },
    { id: 5, text: 'Nausea' },
    { id: 6, text: 'Vision problems, blurring, trouble seeing' },
    { id: 7, text: 'Sensitivity to light' },
    { id: 8, text: 'Hearing difficulty' },
    { id: 9, text: 'Sensitivity to noise' },
    { id: 10, text: 'Numbness or tingling on parts of my body' },
    { id: 11, text: 'Change in taste and/or smell' },
    { id: 12, text: 'Loss of appetite or increased appetite' },
    { id: 13, text: 'Poor concentration, can’t pay attention, easily distracted' },
    { id: 14, text: 'Forgetfulness, can’t remember things' },
    { id: 15, text: 'Difficulty making decisions' },
    { id: 16, text: 'Slowed thinking, difficulty getting organized, can’t finish things' },
    { id: 17, text: 'Fatigue, loss of energy, getting tired easily' },
    { id: 18, text: 'Difficulty falling or staying asleep' },
    { id: 19, text: 'Feeling anxious or tense' },
    { id: 20, text: 'Feeling depressed or sad' },
    { id: 21, text: 'Irritability, easily annoyed' },
    { id: 22, text: 'Poor frustration tolerance, feeling easily overwhelmed by things' },
];

const RESPONSE_LEVELS = [
    { value: 0, label: 'None', description: 'rarely if ever present, not a problem at all' },
    { value: 1, label: 'Mild', description: 'occasionally present, but does not disrupt my activities; I can usually continue what I’m doing; doesn’t really concern me' },
    { value: 2, label: 'Moderate', description: 'often present, occasionally disrupts my activities; I can usually continue what I’m doing with some effort; I feel somewhat concerned' },
    { value: 3, label: 'Severe', description: 'frequently present and disrupts activities; I can only do things that are fairly simple or take little effort; I feel I need help' },
    { value: 4, label: 'Very Severe', description: 'almost always present and I have been unable to perform at work or home, due to this problem; I probably cannot function without help' },
];

const MAX_SCORE = NSI_ITEMS.length * 4;

export default function NSI22() {
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const setAnswer = (itemId: number, value: number) => {
        setAnswers(prev => ({ ...prev, [itemId]: value }));
    };

    const allAnswered = NSI_ITEMS.every(item => answers[item.id] !== undefined);

    const handleSubmit = () => {
        if (!allAnswered) {
            setShowValidation(true);
            return;
        }
        setSubmitted(true);
    };

    const handleRetake = () => {
        setAnswers({});
        setSubmitted(false);
        setShowValidation(false);
    };

    if (submitted) {
        const total = NSI_ITEMS.reduce((sum, item) => sum + answers[item.id], 0);
        return (
            <>
                <div className="breadcrumb">neuroFit &gt; NSI-22</div>
                <div className="title">NSI-22</div>
                <div className="drop-shadow-content-box">
                    <div className="sectionTitle">Results</div>
                    <div className="mrsScoreRow">
                        <div className="mrsScoreLabel">Total Score</div>
                        <div className="mrsScoreBarTrack">
                            <div className="mrsScoreBarFill" style={{ width: `${(total / MAX_SCORE) * 100}%` }} />
                        </div>
                        <div className="mrsScoreValue">{total} / {MAX_SCORE}</div>
                    </div>
                    <button type="button" className="mrsRetakeButton" onClick={handleRetake}>Retake</button>
                </div>
                <p className="mrsCitation">
                    King PR, Donnelly KT, Donnelly JP, Dunnam M, Warner G, Kittleson CJ, Bradshaw CB, Alt M, Meier ST. Psychometric study of the Neurobehavioral Symptom Inventory. <em>J Rehabil Res Dev</em>. 2012. doi:10.1682/JRRD.2011.03.0051
                </p>
            </>
        );
    }

    return (
        <>
            <div className="breadcrumb">neuroFit &gt; NSI-22</div>
            <div className="title">NSI-22</div>
            <div className="drop-shadow-content-box">
                <p className="mrsInstructions">Please rate how much each of the following symptoms disturbed you in the last 2 weeks.</p>
                <ul className="nsiLegend">
                    {RESPONSE_LEVELS.map(level => (
                        <li key={level.value}><strong>{level.value} = {level.label}</strong> &ndash; {level.description}</li>
                    ))}
                </ul>
                {NSI_ITEMS.map(item => (
                    <fieldset
                        key={item.id}
                        className={`mrsItem ${showValidation && answers[item.id] === undefined ? 'mrsItemUnanswered' : ''}`}
                    >
                        <legend className="mrsItemText">{item.text}</legend>
                        <div className="mrsResponseOptions">
                            {RESPONSE_LEVELS.map(level => (
                                <label key={level.value} className="mrsResponseOption">
                                    <input
                                        type="radio"
                                        name={`nsi-item-${item.id}`}
                                        checked={answers[item.id] === level.value}
                                        onChange={() => setAnswer(item.id, level.value)}
                                    />
                                    <span>{level.value} &ndash; {level.label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                ))}
                {showValidation && !allAnswered && (
                    <p className="mrsValidationMessage">Please answer every item before submitting.</p>
                )}
                <button type="button" className="mrsSubmitButton" onClick={handleSubmit}>Submit</button>
            </div>
            <p className="mrsCitation">
                King PR, Donnelly KT, Donnelly JP, Dunnam M, Warner G, Kittleson CJ, Bradshaw CB, Alt M, Meier ST. Psychometric study of the Neurobehavioral Symptom Inventory. <em>J Rehabil Res Dev</em>. 2012. doi:10.1682/JRRD.2011.03.0051
            </p>
        </>
    );
}
